import type { CostTableDetailsGridData } from '@Types/costTable.types';

/**
 * Pure cost calculation helpers for unit cost adjustments.
 * Used by CostTableDetailGrid for percentage-based and fixed-value bulk updates.
 */

/**
 * Rounds a value to cent precision (2 decimal places).
 *
 * `toPrecision(15)` strips the IEEE-754 representation noise that appears
 * past the 15th significant digit (e.g. 35.855 stored as 35.854999999999997)
 * before scaling and rounding, so x.xx5 boundaries round consistently at any
 * realistic cost magnitude without a hand-tuned epsilon.
 */
function roundToCents(value: number): number {
  return Math.round(Number((value * 100).toPrecision(15))) / 100;
}

/**
 * Computes the new unit cost after applying a percentage increase or decrease.
 * Result is rounded to 2 decimal places (cent precision).
 *
 * @param currentUnitCost - Current unit cost value
 * @param percentage - Percentage to apply (e.g. 10 for 10%)
 * @param isIncrease - If true, increase by percentage; if false, decrease
 * @returns Adjusted unit cost (never negative; floor at 0)
 */
export function computeAdjustedUnitCost(
  currentUnitCost: number,
  percentage: number,
  isIncrease: boolean
): number {
  const adjustmentFactor = isIncrease
    ? 1 + percentage / 100
    : 1 - percentage / 100;
  const newCost = currentUnitCost * adjustmentFactor;
  const rounded = roundToCents(newCost);
  return rounded < 0 ? 0 : rounded;
}

/**
 * Row shape required for cost update maps.
 */
export interface CostRowForUpdate {
  id: string;
  assetGroupSet: string;
  description: string;
  assetCount: number;
  unitCost: number;
}

/**
 * Applies a percentage adjustment to selected rows and returns the new updates map.
 * Only includes entries where the new unit cost differs from the original.
 *
 * @param selectedRowIds - IDs of rows to adjust
 * @param baseData - Current grid data (used for current unit cost and row fields)
 * @param originalData - Original data (used to decide if row is "changed")
 * @param currentUpdatedValues - Existing pending updates (new cost is based on current or base)
 * @param percentage - Percentage to apply
 * @param isIncrease - True for increase, false for decrease
 */
export function applyPercentageAdjustment(
  selectedRowIds: string[],
  baseData: CostRowForUpdate[],
  originalData: CostRowForUpdate[],
  currentUpdatedValues: Record<string, CostTableDetailsGridData>,
  percentage: number,
  isIncrease: boolean
): Record<string, CostTableDetailsGridData> {
  const updated = { ...currentUpdatedValues };

  selectedRowIds.forEach(rowId => {
    const baseRow = baseData.find(row => row.id === rowId);
    const originalRow = originalData.find(row => row.id === rowId);

    if (baseRow && originalRow) {
      const currentUnitCost = updated[rowId]?.unitCost ?? baseRow.unitCost;
      const newUnitCost = computeAdjustedUnitCost(
        currentUnitCost,
        percentage,
        isIncrease
      );

      if (newUnitCost !== originalRow.unitCost) {
        updated[rowId] = {
          id: baseRow.id,
          assetGroupSet: baseRow.assetGroupSet,
          description: baseRow.description,
          assetCount: baseRow.assetCount,
          unitCost: newUnitCost,
        };
      } else {
        delete updated[rowId];
      }
    }
  });

  return updated;
}

/**
 * Applies a fixed unit cost to selected rows and returns the new updates map.
 * Only includes entries where the fixed value differs from the original.
 */
export function applyFixedValueUpdate(
  selectedRowIds: string[],
  baseData: CostRowForUpdate[],
  originalData: CostRowForUpdate[],
  currentUpdatedValues: Record<string, CostTableDetailsGridData>,
  newValue: number
): Record<string, CostTableDetailsGridData> {
  const updated = { ...currentUpdatedValues };
  // Round newValue to 2dp before comparing; originalRow.unitCost is assumed to be
  // 2dp-clean (enforced by the backend). Legacy rows with >2dp
  // may be treated as changed on first save, which is acceptable.
  const roundedValue = roundToCents(newValue);

  selectedRowIds.forEach(rowId => {
    const baseRow = baseData.find(row => row.id === rowId);
    const originalRow = originalData.find(row => row.id === rowId);

    if (baseRow && originalRow) {
      if (roundedValue === originalRow.unitCost) {
        delete updated[rowId];
      } else {
        updated[rowId] = {
          id: baseRow.id,
          assetGroupSet: baseRow.assetGroupSet,
          description: baseRow.description,
          assetCount: baseRow.assetCount,
          unitCost: roundedValue,
        };
      }
    }
  });

  return updated;
}
